import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Save } from "lucide-react";
import { adminApi } from "@/lib/lnlApi";
import RichTextEditor from "./RichTextEditor";
import PreviewDialog from "./PreviewDialog";
import { Modal, Field, Toggle, errMsg, inputCls } from "./shared";

const EMPTY = { template_name: "", inquiry_type: "All Inquiry Types", category: "General", subject: "", body: "", is_active: false, signature_id: "", attachment_media_ids: [] };

export default function TemplateEditor({ meta, initial, onClose, onSaved }) {
    const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
    const [sigs, setSigs] = useState([]);
    const [media, setMedia] = useState([]);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    useEffect(() => {
        adminApi.get("/auto-responses/signatures").then((r) => setSigs(r.data)).catch(() => {});
        adminApi.get("/cms/media").then((r) => setMedia((r.data || []).filter((m) => !m.is_archived))).catch(() => {});
    }, []);

    function insertSubject(v) { if (v) set("subject", `${f.subject}{{${v}}}`); }
    function toggleMedia(id) {
        const cur = f.attachment_media_ids || [];
        set("attachment_media_ids", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    }

    async function save() {
        setBusy(true);
        try {
            const body = { ...f, signature_id: f.signature_id || null };
            const { data } = initial?.id
                ? await adminApi.put(`/auto-responses/templates/${initial.id}`, body)
                : await adminApi.post("/auto-responses/templates", body);
            toast.success(initial?.id ? "Template updated" : "Template created");
            onSaved(data);
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    }

    return (
        <Modal title={initial?.id ? `Edit Template · v${initial.version}` : "Create Response Template"} onClose={onClose} wide testid="template-editor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Template Name"><input value={f.template_name} onChange={(e) => set("template_name", e.target.value)} placeholder="Admissions Inquiry Confirmation" className={inputCls} data-testid="tpl-name" /></Field>
                <Field label="Inquiry Type" hint="“All Inquiry Types” works as a fallback for any inquiry.">
                    <select value={f.inquiry_type} onChange={(e) => set("inquiry_type", e.target.value)} className={inputCls} data-testid="tpl-inquiry-type">
                        <option>{meta.all_types}</option>
                        {meta.inquiry_types.map((t) => <option key={t}>{t}</option>)}
                        <option>{meta.status_type}</option>
                    </select>
                </Field>
                <Field label="Category">
                    <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="tpl-category">
                        {meta.categories.map((c) => <option key={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label="Email Signature">
                    <select value={f.signature_id || ""} onChange={(e) => set("signature_id", e.target.value)} className={inputCls} data-testid="tpl-signature">
                        <option value="">No signature</option>
                        {sigs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </Field>
                <div className="md:col-span-2">
                    <Field label="Email Subject">
                        <div className="flex gap-2">
                            <input value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="LNL - We Received Your Inquiry | {{reference_number}}" className={inputCls} data-testid="tpl-subject" />
                            <select value="" onChange={(e) => insertSubject(e.target.value)} className="text-xs rounded-md border border-lnl-border px-2 bg-white" data-testid="tpl-subject-variable">
                                <option value="">+ Variable</option>
                                {meta.variables.filter((v) => v !== "requirements_checklist").map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                    </Field>
                </div>
                <div className="md:col-span-2">
                    <span className="text-xs text-lnl-gray font-heading font-semibold">Email Body</span>
                    <div className="mt-1"><RichTextEditor value={f.body} onChange={(v) => set("body", v)} variables={meta.variables} testid="tpl-body" /></div>
                </div>
                <div className="md:col-span-2">
                    <span className="text-xs text-lnl-gray font-heading font-semibold">Attach LNL documents (from Media Library)</span>
                    <div className="mt-1 max-h-36 overflow-y-auto rounded-md border border-lnl-border divide-y divide-lnl-border" data-testid="tpl-attachments">
                        {media.length === 0 && <div className="p-3 text-xs text-lnl-gray">No files in the Media Library yet.</div>}
                        {media.map((m) => (
                            <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                                <input type="checkbox" checked={(f.attachment_media_ids || []).includes(m.id)} onChange={() => toggleMedia(m.id)} data-testid={`tpl-attach-${m.id}`} />
                                <span className="truncate">{m.title || m.original_filename}</span><span className="text-[11px] text-lnl-gray">{m.kind}</span>
                            </label>
                        ))}
                    </div>
                    <p className="text-[11px] text-lnl-gray mt-1">Attached files are sent as secure download links in the email.</p>
                </div>
                {!initial?.id && meta.can.activate && <div className="md:col-span-2"><Toggle checked={f.is_active} onChange={(v) => set("is_active", v)} label="Activate this template right away" testid="tpl-active" /></div>}
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button onClick={() => setPreview(true)} className="lnl-btn-secondary" data-testid="tpl-preview"><Eye className="h-4 w-4" /> Preview</button>
                <button onClick={save} disabled={busy} className="lnl-btn-primary" data-testid="tpl-save"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Template"}</button>
            </div>
            {preview && <PreviewDialog template={{ ...f, id: initial?.id }} canTest={meta.can.test && !!initial?.id} onClose={() => setPreview(false)} />}
        </Modal>
    );
}
